import classNames from 'classnames'
import React, { FC } from 'react'
import ChevronIcon from '../../../assets/icons/chevron-small.svg'
import { InternalExternalLink } from '../../../types/InternalExternalLink'
import { PortableTextBodyElement } from '../../../utilities/blockContent'
import { isNullOrWhiteSpace } from '../../../utilities/string'
import { ExternalIconSize, Link } from '../../Link'
import { MarketSection } from '../../MarketSection'
import { RichTextArea } from '../../PortableText/RichTextArea'
import { BasicCenterBlockSerializer } from '../../PortableText/serializers/blocks/BasicCenterBlockSerializer'
import { PrimaryLinkSerializer } from '../../PortableText/serializers/marks/PrimaryLinkSerializer'
import './style.css'

export interface DistributorItemProps {
  link?: InternalExternalLink
  text?: string
}

export interface DistributorColumnProps {
  title?: string
  items?: DistributorItemProps[]
}

export interface DistributorListProps {
  id?: string
  columns?: DistributorColumnProps[]
  preamble?: PortableTextBodyElement[]
  title?: string
}

export const DistributorList: FC<DistributorListProps> = ({
  id,
  columns,
  preamble,
  title,
}) => (
  <MarketSection id={id}>
    <div
      className={classNames(
        'col-span-full',
        'sm:mx-120',
        'md:mx-0',
        'lg:col-start-2 lg:col-span-6 lg:-mx-75',
        'xl:col-start-2 xl:col-span-8 xl:-mx-45',
        'xxl:col-start-4 xxl:col-span-8'
      )}
    >
      <div className="flex flex-col justify-center items-center gap-45 p-30 tb:p-45 lg:px-120 lg:py-90">
        <div className="flex flex-col justify-center items-center gap-15 sm:gap-20">
          {!isNullOrWhiteSpace(title) && (
            <h2 className="font-h2 text-h2 text-secondary-darker text-center">{title}</h2>
          )}

          {!!preamble && (
            <div className="flex flex-col gap-20 font-t-light text-t-400 text-neutral-darker lg:text-t-500">
              <RichTextArea
                components={{
                  block: BasicCenterBlockSerializer,
                  marks: {
                    link: PrimaryLinkSerializer,
                  },
                }}
                value={preamble}
              />
            </div>
          )}
        </div>
        <div className="w-full sm:w-auto flex justify-center">
          <div className="distributor-list-columns flex flex-col justify-between w-full flex-wrap tb:flex-row sm:flex-nowrap">
            {!!columns &&
              columns.map((col, index) => <DistributorColumn {...col} key={index} />)}
          </div>
        </div>
      </div>
    </div>
  </MarketSection>
)

export const DistributorColumn = (column: DistributorColumnProps) => (
  <div className="flex flex-col gap-15 sm:gap-15 w-full tb:w-1/2 pb-20 tb:pl-20 tb:pr-20 sm:w-[13rem] md:w-[15rem] lg:w-[20rem]">
    <h3 className="font-t-bold lg:text-t-500 text-secondary-darker">{column.title}</h3>
    {!!column.items && (
      <ul className="flex flex-col gap-20">
        {column.items.map((item, index) => (
          <li key={index} className="flex flex-col gap-10">
            {!!item.link && !isNullOrWhiteSpace(item.link.title) && (
              <Link
                {...item.link}
                externalIconSize={ExternalIconSize.Md}
                className="text-primary flex flex-wrap items-baseline"
              >
                {item.link.title}
                {!item.link.isExternal && (
                  <ChevronIcon
                    className={classNames(
                      'w-[0.6rem] h-[0.6rem] flex-none ml-8 fill-current rotate-180'
                    )}
                  />
                )}
              </Link>
            )}
            {!isNullOrWhiteSpace(item.text) && (
              <p className="font-t-light text-t-300 text-neutral-darker">{item.text}</p>
            )}
          </li>
        ))}
      </ul>
    )}
  </div>
)
